import posthog from 'posthog-js';

/**
 * Knowing that something broke for somebody else.
 *
 * Until now the only signal was a person writing to say so — and most people
 * do not write, they uninstall. Fourteen days of closed testing with twelve
 * strangers would have produced almost nothing but silence.
 *
 * WHAT IS SENT: the message, where in the code it happened, and which screen
 * the person was on. Nothing else.
 *
 * WHAT IS NEVER SENT — and this is the part that matters, because these reports
 * come from real calendars: no event titles, no notes, no names, no email
 * addresses, no place names. An error message can easily contain them ("cannot
 * save 'Dentist 15:30'"), so everything that looks like an address, a quoted
 * phrase, or a long number is stripped before anything leaves the phone, and
 * the url is reduced to the shape of the route.
 *
 * It is also deliberately quiet: at most a handful of reports per session, and
 * the same fault is only ever reported once. A loop that fires an error sixty
 * times a second must not turn into sixty network requests a second.
 */

const LIVE = typeof window !== 'undefined'
  && /(^|\.)memocal\.app$/.test(window.location.hostname);

const MAX_PER_SESSION = 8;
let sent = 0;
const alreadySeen = new Set();

/** Strip anything that could be somebody's private life out of a string. */
export function scrub(text) {
  return String(text || '')
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '<email>')
    // Quoted fragments are almost always user content echoed back by an error.
    .replace(/'[^']{3,}'/g, "'…'")
    .replace(/"[^"]{3,}"/g, '"…"')
    .replace(/«[^»]{3,}»/g, '«…»')
    // Long digit runs: ids, phone numbers, tokens.
    .replace(/\b\d{5,}\b/g, '<n>')
    // Anything that looks like a token or an event id.
    .replace(/\b[A-Za-z0-9_-]{20,}\b/g, '<id>')
    .slice(0, 300);
}

/** '/edit/6h3k2l9m' → '/edit/:id' — the shape of the screen, not which event. */
export function routeShape(pathname) {
  return String(pathname || '/')
    .split('/')
    .map((seg) => {
      if (!seg) return seg;
      if (/^\d+$/.test(seg)) return ':id';
      if (seg.length >= 12) return ':id';
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(seg)) return ':id';
      return seg;
    })
    .join('/') || '/';
}

/** Only our own frames, without the full urls. Enough to find the line. */
function trimStack(stack) {
  return String(stack || '')
    .split('\n')
    .slice(0, 4)
    .map((line) => line.replace(/https?:\/\/[^\s)]+\//g, '').trim())
    .join(' | ')
    .slice(0, 400);
}

/**
 * Report one fault.
 * @param {string} kind  where it was caught: 'render' | 'window' | 'promise'
 */
export function reportError(kind, error, extra = {}) {
  try {
    const message = scrub(error?.message || error || 'unknown');
    // One report per distinct fault: a broken render loop must not flood.
    const fingerprint = kind + '|' + message;
    if (alreadySeen.has(fingerprint)) return;
    alreadySeen.add(fingerprint);
    if (sent >= MAX_PER_SESSION) return;
    sent++;

    const payload = {
      kind,
      message,
      where: trimStack(error?.stack),
      route: routeShape(window.location?.pathname),
      ...extra,
    };

    if (!LIVE) {
      // In development, be useful rather than silent.
      // eslint-disable-next-line no-console
      console.warn('[MeMo] error report (not sent, not live):', payload);
      return;
    }
    posthog.capture('app_error', payload);
  } catch (_) {
    // Reporting a fault must never be able to cause one.
  }
}

/** Catch what escapes React: plain scripts and rejected promises. */
export function installErrorReporting() {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (e) => {
    // Failed <img>/<script> loads also fire this, without an Error object.
    if (!e?.error) return;
    reportError('window', e.error);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e?.reason;
    // A rejected API call is not a fault: the screens handle those themselves.
    if (r?.isAxiosError) return;
    reportError('promise', r instanceof Error ? r : new Error(String(r)));
  });
}
